'use client'

import {useEffect, useId, useRef, useState} from 'react'
import {useRouter} from 'next/navigation'
import {useTranslations} from 'next-intl'
import {MessageSquareHeart} from 'lucide-react'
import {Button} from '@/design-system'
import {api} from '@/lib/api/client'
import type {Me, SurveyPrompt} from '@/lib/api/types'
import './survey-prompt.css'
import '../confirm-dialog.css'

/** Let the results paint first; a dialog over a blank page reads as a wall. */
const OPEN_DELAY_MS = 1200

/**
 * "Got 3 minutes?" — shown on the results page once the feedback survey is
 * due (`/me.survey`). It re-reads `/me` in the browser rather than trusting
 * the server render: that render fetched `/me` *before* the search that
 * made the survey due, so on the very first search it would say "not yet".
 */
export function SurveyPromptDialog({
  locale,
  initial
}: {
  locale: string
  initial: SurveyPrompt | null | undefined
}) {
  const t = useTranslations('survey')
  const router = useRouter()
  const ref = useRef<HTMLDialogElement>(null)
  const headingId = useId()
  const [prompt, setPrompt] = useState<SurveyPrompt | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    // Finished or turned down: nothing will change by searching again.
    if (initial && (initial.state === 'done' || initial.state === 'dismissed')) return
    let cancelled = false
    const timer = window.setTimeout(async () => {
      try {
        const me = await api.get<Me>('/me')
        const current = me.survey
        if (cancelled || !current || (current.state !== 'due' && current.state !== 'started')) return
        if (current.state === 'due') await api.post(`/survey/prompted?locale=${locale}`)
        if (!cancelled) setPrompt(current)
      } catch {
        // The survey is a nice-to-have; a failure here must never surface.
      }
    }, OPEN_DELAY_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [initial, locale])

  useEffect(() => {
    const dialog = ref.current
    if (!dialog || !prompt || dialog.open) return
    if (typeof dialog.showModal === 'function') dialog.showModal()
    else dialog.setAttribute('open', '')
  }, [prompt])

  async function later() {
    setBusy(true)
    await api.post('/survey/dismiss').catch(() => undefined)
    ref.current?.close()
    setPrompt(null)
    setBusy(false)
  }

  function start() {
    const here = `${window.location.pathname}${window.location.search}`
    router.push(`/${locale}/survey?return=${encodeURIComponent(here)}`)
  }

  if (!prompt) return null
  const resuming = prompt.state === 'started'

  return (
    <dialog
      ref={ref}
      className="u-confirm u-sv-prompt"
      aria-labelledby={headingId}
      onCancel={(event) => {
        event.preventDefault()
        if (!busy) void later()
      }}
    >
      <div className="u-confirm__body">
        <span className="u-sv-prompt__icon" aria-hidden="true">
          <MessageSquareHeart size={24} />
        </span>
        <h2 id={headingId}>{t('promptTitle')}</h2>
        <p>{resuming ? t('promptResumeBody') : t('promptBody')}</p>
        <div className="u-confirm__actions">
          <Button variant="secondary" onClick={later} disabled={busy}>
            {t('promptLater')}
          </Button>
          <Button onClick={start} disabled={busy} autoFocus>
            {resuming ? t('promptResume') : t('promptStart')}
          </Button>
        </div>
      </div>
    </dialog>
  )
}
